import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Users,
  Key,
  CircleNotch,
  Copy,
  CheckCircle,
} from '@phosphor-icons/react';
import AdminLayout from '../components/AdminLayout';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import API_URL from '../config';
import { toast } from 'sonner';

export default function AdminTeam() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resettingId, setResettingId] = useState(null);
  const [credentials, setCredentials] = useState(null); // { email, name, password }
  const [copiedField, setCopiedField] = useState('');

  const fetchTeam = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/team`);
      setTeam(response.data || []);
    } catch (e) {
      toast.error('Failed to load teammates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTeam(); }, []);

  const handleResetPassword = async (teammate) => {
    if (!window.confirm(`Reset password for ${teammate.name}? The new password will be shown once — make sure to copy it.`)) return;
    setResettingId(teammate.id);
    try {
      const response = await axios.post(`${API_URL}/api/admin/team/${teammate.id}/reset-password`, {});
      setCredentials({
        email: response.data.email,
        name: response.data.name,
        password: response.data.password,
      });
      toast.success('Password reset');
    } catch (e) {
      toast.error('Failed to reset password');
    } finally {
      setResettingId(null);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedField(''), 2000);
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto" data-testid="admin-team-page">
        <div className="mb-8">
          <p className="label-uppercase mb-1" style={{ color: '#1d57a5' }}>Access Management</p>
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: '#141b4d' }}>Admin Team</h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: '#52525B' }}>
            Everyone here has full admin access. To rotate a teammate's password, click the key icon — the new password shows once and cannot be retrieved later.
          </p>
        </div>

        {loading ? (
          <div className="card-swiss text-center py-10" style={{ color: '#9a9488' }}>Loading team…</div>
        ) : team.length === 0 ? (
          <div className="card-swiss text-center py-10" style={{ color: '#9a9488' }}>No admins found.</div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            {team.map((t) => (
              <div
                key={t.id}
                className="card-swiss flex items-center justify-between flex-wrap gap-4"
                data-testid={`teammate-row-${t.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: '#f2e9db' }}>
                    <Users weight="duotone" className="w-6 h-6" style={{ color: '#141b4d' }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium" style={{ color: '#101820' }}>{t.name || '—'}</p>
                      {t.is_you && (
                        <span className="text-xs font-medium px-2 py-0.5" style={{ backgroundColor: '#ffd100', color: '#141b4d' }}>
                          YOU
                        </span>
                      )}
                    </div>
                    <p className="text-sm" style={{ color: '#9a9488' }}>{t.email}</p>
                  </div>
                </div>
                <div>
                  <button
                    onClick={() => handleResetPassword(t)}
                    disabled={resettingId === t.id}
                    className="btn-secondary inline-flex items-center gap-2 text-sm"
                    data-testid={`reset-admin-password-${t.id}`}
                  >
                    {resettingId === t.id ? (
                      <><CircleNotch weight="bold" className="w-4 h-4 animate-spin" /> Resetting…</>
                    ) : (
                      <><Key weight="regular" className="w-4 h-4" /> Reset password</>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {/* Credentials dialog */}
        <Dialog open={!!credentials} onOpenChange={(open) => !open && setCredentials(null)}>
          <DialogContent data-testid="admin-credentials-dialog">
            <DialogHeader>
              <DialogTitle style={{ color: '#141b4d' }}>New password for {credentials?.name}</DialogTitle>
              <DialogDescription>
                This password will not be shown again. Copy it now and share it securely.
              </DialogDescription>
            </DialogHeader>
            {credentials && (
              <div className="space-y-4 mt-4">
                <div className="callout-box">
                  <p className="text-sm font-medium mb-1" style={{ color: '#141b4d' }}>Save these credentials — the password cannot be retrieved later.</p>
                  <p className="text-xs mt-2" style={{ color: '#52525B' }}>Best practice: click <strong>Copy</strong> rather than typing the password by hand.</p>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider" style={{ color: '#9a9488' }}>Email</label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 px-3 py-2 font-mono text-sm" style={{ backgroundColor: '#f2e9db', color: '#141b4d' }}>
                      {credentials.email}
                    </code>
                    <button
                      onClick={() => copyToClipboard(credentials.email, 'email')}
                      className="btn-secondary inline-flex items-center gap-1 text-sm px-3 py-2"
                      data-testid="copy-admin-email-btn"
                    >
                      {copiedField === 'email' ? <CheckCircle weight="bold" className="w-4 h-4" /> : <Copy weight="regular" className="w-4 h-4" />}
                      Copy
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider" style={{ color: '#9a9488' }}>Password</label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 px-3 py-2 font-mono text-sm" style={{ backgroundColor: '#f2e9db', color: '#141b4d' }}>
                      {credentials.password}
                    </code>
                    <button
                      onClick={() => copyToClipboard(credentials.password, 'password')}
                      className="btn-secondary inline-flex items-center gap-1 text-sm px-3 py-2"
                      data-testid="copy-admin-password-btn"
                    >
                      {copiedField === 'password' ? <CheckCircle weight="bold" className="w-4 h-4" /> : <Copy weight="regular" className="w-4 h-4" />}
                      Copy
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => setCredentials(null)}
                  className="btn-primary w-full"
                  data-testid="close-admin-credentials-btn"
                >
                  Done
                </button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
